import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, FileText, ClipboardCheck, Receipt, LockOpen, Loader2, Cpu, Activity, RefreshCw } from "lucide-react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleString("cs-CZ") : "—");

const Th = ({ children }) => <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-sm text-slate-700 ${className}`}>{children}</td>;

const TableShell = ({ children, testId }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
    <table className="w-full" data-testid={testId}>{children}</table>
  </div>
);

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [audits, setAudits] = useState([]);
  const [docs, setDocs] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [activity, setActivity] = useState([]);

  const loadActivity = () => api.get("/admin/activity").then((r) => setActivity(r.data)).catch(() => {});

  const loadAll = () => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/audits").then((r) => setAudits(r.data)).catch(() => {});
    api.get("/admin/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/admin/purchases").then((r) => setPurchases(r.data)).catch(() => {});
    loadActivity();
  };

  useEffect(() => {
    if (user?.role === "admin") loadAll();
  }, [user]);

  if (user && user.role !== "admin") {
    return <Layout><p className="py-20 text-center text-sm text-slate-500">Tato sekce je dostupná pouze administrátorům.</p></Layout>;
  }
  if (!overview) {
    return <Layout><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div></Layout>;
  }

  const unlock = async (id) => {
    try {
      await api.post(`/admin/audits/${id}/unlock`);
      toast.success("Audit odemčen");
      loadAll();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const STATS = [
    { label: "Uživatelé", value: overview.users, icon: Users },
    { label: "Dokumenty ŠVP", value: overview.documents, icon: FileText },
    { label: "Audity (dokončené)", value: `${overview.audits} (${overview.audits_done})`, icon: ClipboardCheck },
    { label: "Zaplacené audity", value: overview.audits_paid, icon: LockOpen },
    { label: "Tržby", value: `${overview.revenue_czk.toLocaleString("cs-CZ")} Kč`, icon: Receipt },
  ];

  return (
    <Layout>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Administrace</h1>
      <p className="mt-1 text-sm text-slate-500">Kompletní správa uživatelů, auditů, dokumentů, plateb a AI modelu.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="admin-stats">
        {STATS.map((s) => (
          <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <s.icon className="h-3.5 w-3.5 text-emerald-600" /> {s.label}
            </div>
            <div className="mt-1.5 text-2xl font-extrabold text-slate-900">{s.value}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="audits" className="mt-8">
        <TabsList>
          <TabsTrigger value="audits" data-testid="admin-tab-audits">Audity ({audits.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Uživatelé ({users.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="admin-tab-documents">Dokumenty ({docs.length})</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="admin-tab-purchases">Platby ({purchases.length})</TabsTrigger>
          <TabsTrigger value="activity" data-testid="admin-tab-activity">Aktivita ({activity.length})</TabsTrigger>
          <TabsTrigger value="ai" data-testid="admin-tab-ai">AI model</TabsTrigger>
        </TabsList>

        <TabsContent value="audits" className="mt-4">
          <TableShell testId="admin-audits-table">
            <thead><tr className="border-b border-slate-100 bg-slate-50">
              <Th>Dokument</Th><Th>Uživatel</Th><Th>Stav</Th><Th>Tier</Th><Th>Výsledek</Th><Th>Náklady AI</Th><Th>Placeno</Th><Th>Vytvořeno</Th><Th>Akce</Th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {audits.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <Td className="font-medium text-slate-900">{a.document_filename || "—"}</Td>
                  <Td>{a.user_email}{a.source === "public_funnel" && <span className="ml-1 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">funnel</span>}</Td>
                  <Td>{a.status}</Td>
                  <Td>{a.tier === "quick"
                    ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">rychlá</span>
                    : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">plná</span>}</Td>
                  <Td className="whitespace-nowrap text-xs">{a.summary ? `${a.summary.critical_count} krit. · ${a.summary.high_count} záv. · ${a.summary.medium_count} stř.` : "—"}</Td>
                  <Td className="whitespace-nowrap text-xs" title={a.usage ? `${a.usage.calls} volání · ${a.usage.model} · ${a.usage.reasoning_effort || ""}` : ""}>
                    {a.usage?.cost_usd != null ? `$${a.usage.cost_usd.toFixed(3)}` : "—"}</Td>
                  <Td>{a.paid ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">Ano</span>
                    : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">Ne</span>}</Td>
                  <Td className="whitespace-nowrap text-xs">{fmtDate(a.created_at)}</Td>
                  <Td>
                    <div className="flex gap-1.5 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/audits/${a.id}`)} data-testid={`admin-open-audit-${a.id}`}>Otevřít</Button>
                      {!a.paid && a.status === "done" && (
                        <Button size="sm" variant="outline" onClick={() => unlock(a.id)} data-testid={`admin-unlock-${a.id}`}
                          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">Odemknout</Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <TableShell testId="admin-users-table">
            <thead><tr className="border-b border-slate-100 bg-slate-50">
              <Th>E-mail</Th><Th>Jméno</Th><Th>Role</Th><Th>Telefon</Th><Th>Zdroj</Th><Th>Auditů</Th><Th>Registrace</Th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} className="cursor-pointer hover:bg-slate-50" data-testid={`admin-user-row-${u.id}`}
                  onClick={() => navigate(`/admin/users/${u.id}`)}>
                  <Td className="font-medium text-emerald-700 underline underline-offset-2">{u.email}</Td>
                  <Td>{u.name || "—"}</Td>
                  <Td>{u.role === "admin" ? <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-bold text-white">admin</span> : "uživatel"}</Td>
                  <Td className="whitespace-nowrap">{u.phone || "—"}{u.phone_verified && <span className="ml-1 text-emerald-600">✓</span>}</Td>
                  <Td>{u.source === "public_funnel" ? "funnel" : "aplikace"}</Td>
                  <Td>{u.audit_count}</Td>
                  <Td className="whitespace-nowrap text-xs">{fmtDate(u.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <TableShell testId="admin-documents-table">
            <thead><tr className="border-b border-slate-100 bg-slate-50">
              <Th>Soubor</Th><Th>Typ</Th><Th>Stran</Th><Th>Uživatel</Th><Th>Nahráno</Th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {docs.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <Td className="font-medium text-slate-900">{d.filename}</Td>
                  <Td>{d.file_type}</Td>
                  <Td>{d.page_count}</Td>
                  <Td>{d.user_email}</Td>
                  <Td className="whitespace-nowrap text-xs">{fmtDate(d.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        </TabsContent>

        <TabsContent value="purchases" className="mt-4">
          <TableShell testId="admin-purchases-table">
            <thead><tr className="border-b border-slate-100 bg-slate-50">
              <Th>Faktura</Th><Th>Organizace</Th><Th>E-mail</Th><Th>Telefon</Th><Th>Stav</Th><Th>Zaplaceno</Th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {purchases.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <Td className="font-medium text-slate-900">
                    {p.invoice?.number || "—"}
                    {p.invoice?.mock && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">DEV</span>}
                    {p.invoice?.public_url && <a className="ml-2 text-xs text-emerald-700 underline" href={p.invoice.public_url} target="_blank" rel="noreferrer">otevřít</a>}
                  </Td>
                  <Td>{p.billing_name || "—"}</Td>
                  <Td>{p.email}</Td>
                  <Td className="whitespace-nowrap">{p.phone}</Td>
                  <Td>{p.status}</Td>
                  <Td className="whitespace-nowrap text-xs">{fmtDate(p.paid_at)}</Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs text-slate-500">Posledních {activity.length} událostí — přihlášení, registrace, nahrání dokumentů, audity, SMS, faktury i pokusy botů (honeypot).</p>
            <Button size="sm" variant="outline" onClick={loadActivity} data-testid="admin-refresh-activity">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Obnovit
            </Button>
          </div>
          <ActivityTable rows={activity} />
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          <AiSettings />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

const EVENT_LABELS = {
  login_success: ["Přihlášení", "bg-emerald-100 text-emerald-800"],
  login_failed: ["Neúspěšné přihlášení", "bg-amber-100 text-amber-800"],
  honeypot_triggered: ["BOT (honeypot)", "bg-red-600 text-white"],
  user_registered: ["Registrace", "bg-emerald-100 text-emerald-800"],
  public_intake_verified: ["Registrace (funnel)", "bg-sky-100 text-sky-800"],
  intake_sms_sent: ["SMS odeslána", "bg-slate-100 text-slate-700"],
  purchase_sms_sent: ["SMS (nákup)", "bg-slate-100 text-slate-700"],
  document_uploaded: ["Nahrán dokument", "bg-blue-100 text-blue-800"],
  audit_started: ["Audit spuštěn", "bg-blue-100 text-blue-800"],
  audit_finished: ["Audit dokončen", "bg-emerald-100 text-emerald-800"],
  audit_cancelled: ["Audit zrušen", "bg-slate-100 text-slate-600"],
  invoice_issued: ["Faktura vystavena", "bg-violet-100 text-violet-800"],
  audit_unlocked: ["Audit odemčen", "bg-emerald-600 text-white"],
  export: ["Export reportu", "bg-slate-100 text-slate-700"],
  human_review: ["Lidské ověření", "bg-slate-100 text-slate-700"],
  agent_handoff: ["Předání AgentŠVP", "bg-indigo-100 text-indigo-800"],
  checklist_import: ["Import checklistu", "bg-slate-100 text-slate-700"],
  framework_pack_import: ["Import frameworku", "bg-slate-100 text-slate-700"],
};

const HIDDEN_KEYS = new Set(["id", "type", "at", "email", "user_id"]);

const detailOf = (r) =>
  Object.entries(r)
    .filter(([k, v]) => !HIDDEN_KEYS.has(k) && v !== null && v !== "")
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");

const ActivityTable = ({ rows }) => (
  <TableShell testId="admin-activity-table">
    <thead><tr className="border-b border-slate-100 bg-slate-50">
      <Th>Čas</Th><Th>Událost</Th><Th>E-mail</Th><Th>Detail</Th>
    </tr></thead>
    <tbody className="divide-y divide-slate-100">
      {rows.length === 0 && (
        <tr><Td className="py-8 text-center text-slate-400" colSpan={4}>Zatím žádné události</Td></tr>
      )}
      {rows.map((r) => {
        const [label, cls] = EVENT_LABELS[r.type] || [r.type, "bg-slate-100 text-slate-700"];
        return (
          <tr key={r.id} className="hover:bg-slate-50">
            <Td className="whitespace-nowrap text-xs">{fmtDate(r.at)}</Td>
            <Td><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${cls}`}>{label}</span></Td>
            <Td className="whitespace-nowrap">{r.email || "—"}</Td>
            <Td className="max-w-md truncate text-xs text-slate-500" title={detailOf(r)}>{detailOf(r) || "—"}</Td>
          </tr>
        );
      })}
    </tbody>
  </TableShell>
);

const AiSettings = () => {
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("");
  const [quickModel, setQuickModel] = useState("gpt-5-nano");
  const [effortFree, setEffortFree] = useState("low");
  const [effortPaid, setEffortPaid] = useState("medium");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setProvider(r.data.model_provider); setModel(r.data.model_name);
      setQuickModel(r.data.quick_model_name || "gpt-5-nano");
      setEffortFree(r.data.reasoning_effort_free || "low");
      setEffortPaid(r.data.reasoning_effort_paid || "medium");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        model_provider: provider, model_name: model, quick_model_name: quickModel,
        reasoning_effort_free: effortFree, reasoning_effort_paid: effortPaid,
      });
      toast.success("Nastavení AI uloženo — platí pro nové audity");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const EFFORTS = ["minimal", "low", "medium", "high"];
  return (
    <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-ai-settings">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Cpu className="h-4 w-4 text-emerald-600" /> AI model pro audit engine</div>
      <p className="mt-1 text-xs text-slate-500">Mění pouze administrátor — platí pro všechny nové audity.</p>
      <div className="mt-4 space-y-3">
        <select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="admin-provider-select"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="openai">OpenAI (přímo)</option>
          <option value="edenai">EdenAI (agregátor)</option>
        </select>
        <div>
          <label className="text-xs font-semibold text-slate-600">Model — placený plný audit</label>
          <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="gpt-5-mini" data-testid="admin-model-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Model — rychlá prohlídka zdarma</label>
          <Input value={quickModel} onChange={(e) => setQuickModel(e.target.value)} placeholder="gpt-5-nano" data-testid="admin-quick-model-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Reasoning — free</label>
            <select value={effortFree} onChange={(e) => setEffortFree(e.target.value)} data-testid="admin-effort-free-select"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              {EFFORTS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Reasoning — placený</label>
            <select value={effortPaid} onChange={(e) => setEffortPaid(e.target.value)} data-testid="admin-effort-paid-select"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              {EFFORTS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
        </div>
        <p className="text-[11px] text-slate-400">Vyšší reasoning = důkladnější (a dražší, pomalejší) uvažování modelu. Spotřebu a cenu každého auditu vidíte v záložce Audity.</p>
        <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-save-model-button">Uložit</Button>
        <div className="border-t border-slate-100 pt-3">
          <Button variant="outline" size="sm" data-testid="admin-create-demo-button"
            onClick={async () => {
              try {
                await api.post("/admin/demo-audit");
                toast.success("Ukázkový audit se generuje — za pár minut bude na /ukazka");
              } catch (e) { toast.error(errMsg(e)); }
            }}>
            Vygenerovat ukázkový audit (/ukazka)
          </Button>
          <p className="mt-1 text-[11px] text-slate-400">Spustí plný audit fiktivní MŠ Sluníčko a zveřejní ho jako ukázku na landing page.</p>
        </div>
      </div>
    </div>
  );
};
